import type React from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { Helmet } from "react-helmet-async";
import { APP_ROUTES } from "./routes";

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll the app's scroll container (not window) to top
    requestAnimationFrame(() => {
      const scrollContainer = document.querySelector(".overflow-y-auto");
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: "instant" });
      }
    });
  }, []);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background">
      <Helmet>
        <title>Privacy Policy — MycenAI</title>
        <meta name="description" content="How MycenAI protects your clinical data. Built on NEAR AI's Trusted Execution Environment with hardware-secured processing." />
        <link rel="canonical" href="https://mycenai.org/privacy/" />
      </Helmet>
      <div className="mx-auto w-full max-w-3xl px-4 py-12">
        <button
          onClick={() => navigate(APP_ROUTES.SPLASH)}
          className="mb-8 text-muted-foreground text-sm hover:text-foreground"
        >
          ← Back
        </button>

        <div className="prose prose-sm dark:prose-invert max-w-none">
          <h1 className="mb-6 font-bold text-3xl">Privacy Policy - MycenAI</h1>

          <h2 className="mt-8 mb-4 font-semibold text-xl">How We Protect Your Data</h2>
          <p className="mb-4">MycenAI uses NEAR AI Cloud's confidential computing infrastructure:</p>

          <div className="space-y-4">
            <div>
              <p className="font-semibold">Processing:</p>
              <p>
                All AI inference is performed inside Intel TDX and NVIDIA Confidential Computing hardware (Trusted
                Execution Environments). During AI processing, your data is isolated in hardware-encrypted memory that
                is inaccessible to the infrastructure operator.
              </p>
            </div>

            <div>
              <p className="font-semibold">Storage:</p>
              <p>
                MycenAI does not store clinical conversation content on its own infrastructure. Conversation history
                is stored within NEAR AI's TEE-protected infrastructure, encrypted at rest using hardware-managed keys.
              </p>
            </div>

            <div>
              <p className="font-semibold">Verification:</p>
              <p>
                Cryptographic attestation verifies that AI processing occurred in a genuine, unmodified Trusted Execution
                Environment. You can inspect verification status directly in the app.
              </p>
            </div>

            <div>
              <p className="font-semibold">Our Infrastructure:</p>
              <p>
                MycenAI operates a secure gateway that authenticates your identity and routes requests to NEAR AI's
                TEE infrastructure. We store only conversation metadata (IDs, timestamps) — no clinical content.
              </p>
            </div>

            <div>
              <p className="font-semibold">No Training on Your Data:</p>
              <p>
                We do not use your content to train, retrain, or fine-tune our models under any circumstances. Your clinical notes remain yours alone.
              </p>
            </div>
          </div>

          <h2 className="mt-8 mb-4 font-semibold text-xl">Beta Status</h2>
          <p className="mb-4">
            MycenAI is currently in beta. We are finalizing our Business Associate Agreement (BAA) for
            full HIPAA compliance.
          </p>

          <h2 className="mt-8 mb-4 font-semibold text-xl">Contact</h2>
          <p className="mb-4">Questions? Email: support@mycenai.org</p>

          <p className="mt-8 text-muted-foreground text-sm">Last updated: February 12, 2026</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
